import { APILookupValue, APIObject } from "@/services/api/types";
import { IcpFit, IcpMetadata } from "@/entities/icpFit/types";
import { ISOStringFormat } from "date-fns";

export enum LeadColumn {
  NAME = "name",
  COMPANY_NAME = "companyName",
  PRODUCT_NAME = "productName",
  SDR_OWNER = "sdrOwner",
  AE_OWNER = "aeOwner",
  STATUS = "status",
  OPEN_FOR_DAYS = "openForDays",
  LICENSE_ID = "licenseId",
  NUMBER_OF_LICENSES = "numberOfLicenses",
  SOURCE = "source",
  SALES_EXPLORATION_MEETING_DATE = "salesExplorationMeetingDate",
  CONTACT = "contact",
  CONTACT_JOB_TITLE = "contactJobTitle",
  CONTACT_EMAIL = "contactEmail",
  CREATED_AT = "createdAt",
  CLOSED_AT = "closedAt",
  TOKEN_HOLDER = "tokenHolder",
  SALES_EXPLORATION_STATUS = "salesExplorationStatus"
}

export enum LeadStatus {
  DISQUALIFIED = 1,
  CLOSED_QUALIFIED,
  CONVERTED,
  STUCK,
  IN_PROGRESS,
  CONFIRM_ICP
}

export enum LeadStatusNoIcp {
  DISQUALIFIED = LeadStatus.DISQUALIFIED,
  CLOSED_QUALIFIED = LeadStatus.CLOSED_QUALIFIED,
  CONVERTED = LeadStatus.CONVERTED,
  STUCK = LeadStatus.STUCK,
  IN_PROGRESS = LeadStatus.IN_PROGRESS
}

export const LeadOpenStatuses = [
  LeadStatus.STUCK,
  LeadStatus.IN_PROGRESS,
  LeadStatus.CONFIRM_ICP
];

// Statuses a deal suggestion can be acted on in; mirrors the backend's eligible
// set, which leaves out CONFIRM_ICP.
export const LeadDealSuggestionStatuses = [
  LeadStatus.IN_PROGRESS,
  LeadStatus.STUCK
];

export enum SalesExplorationStatus {
  NOT_BOOKED = "not_booked",
  BOOKED = "booked",
  DONE = "done"
}

export enum LeadEngagementScore {
  COLD = 1,
  LUKEWARM,
  ON_FIRE
}

export enum LeadType {
  INBOUND = 1,
  OUTBOUND,
  REFERRAL
}

export enum DisqualifiedReasons {
  UNQUALIFIED_COMPANY = 1,
  UNQUALIFIED_PERSON,
  DUPLICATE_LEAD,
  MEETING_TOO_SHORT,
  SALES_EXPLORATION_MEETING
}

export interface LeadSourcesResponse {
  sources: APILookupValue[];
}

interface LeadData extends APIObject {
  name: string;
  sdrOwner: string | null;
  aeOwner: string | null;
  companyId: string;
  companyName?: string;
  contactId: string;
  contactEmail?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactJobTitle?: string;
  status: LeadStatus;
  statusResolved: string | null;
  openForDays: number | null;
  engagementScore: LeadEngagementScore | null;
  conversionProbability: number | null;
  type: LeadType | null;
  source: number | null;
  statusNotes?: string;
  disqualifiedReason?: number | null;
  disqualifiedCompanyReason?: number | null;
  closedAsQualifiedReason?: number | null;
  statusChangedAt?: string | null;
  statusChangedBy?: string | null;
  productId: string | null;
  productName?: string | null;
  licenseId: string | null;
  numberOfLicenses: number | null;
  inboundCampaignType: number | null;
  outboundCampaignType: number | null;
  salesExplorationMeetingDate: ISOStringFormat | null;
  referringCompany: string | null;
  referringPerson: string | null;
  otherCompanyName: string | null;
  otherPersonName: string | null;
  lastActivity: ISOStringFormat | null;
  salesExplorationStatus: SalesExplorationStatus | null;
  salesExplorationStatusDisplay: string | null;
  contactIcpFit?: IcpFit | null;
  companyIcpFit?: IcpFit | null;
  contactIcpMetadata?: IcpMetadata | null;
  companyIcpMetadata?: IcpMetadata | null;
}

interface LeadsPage {
  leads: LeadData[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export type disqualifyModalMode = "closedAsQualified" | "disqualified";

export interface LeadDisqualifiedReasonsResponse {
  disqualifiedReasons: APILookupValue[];
}

export interface LeadClosedAsQualifiedReasonsResponse {
  closedAsQualifiedReasons: APILookupValue[];
}

export interface LeadDisqualifiedCompanyReasonsResponse {
  disqualifiedCompanyReasons: APILookupValue[];
}

export interface LeadTypesResponse {
  leadsTypes: APILookupValue[];
}

export interface CampaignType {
  id: number;
  name: string;
  order: number;
  createdAt: string;
}

export interface LeadInboundCampaignTypesResponse {
  inboundCampaignTypes: CampaignType[];
}

export interface LeadOutboundCampaignTypesResponse {
  outboundCampaignTypes: CampaignType[];
}

export type LeadDealSuggestionStatus = "active" | "dismissed" | "cleared";

export interface LeadDealSuggestion {
  id: string;
  leadId: string;
  status: LeadDealSuggestionStatus;
  // True only while the suggestion is active and the lead is still open to convert.
  isActionable: boolean;
  leadStatus?: number | null;
  meetingOrdinal: number;
  meetingDate?: string | null;
  triggerActivityId: string;
  personId?: string | null;
  personName?: string | null;
  personEmail?: string | null;
  suggestedOwnerId?: string | null;
  activatedAt?: string | null;
  dismissCount: number;
}

export type { LeadData, LeadsPage };
